import React, { useMemo, useState } from "react";
import { X, Users, MessagesSquare } from "lucide-react";
import AvatarRing from "ui/AvatarRing";
import { Button } from "ui/Button";
import { Input } from "ui/Input";
import LoadingSkeleton from "ui/LoadingSkeleton";
import ModalOrDrawer from "ui/ModalOrDrawer";
import EmptyState from "ui/EmptyState";
import { useChatRecipientSearchQuery, useCreateChatConversationMutation } from "hooks/queries/useChatQueries";
import type { ChatConversation, ChatRecipientSearchResult } from "types/chat";
import {
  getActorInitials,
  resolveActorAvatarUrl,
} from "util/actorIdentity";
import { ACTOR_AVATAR_SIZES } from "util/actorAvatarSizes";
import type { Actor } from "types/actor";

/**
 * Coerce a recipient search result into an `Actor` so we can hand it
 * to the standardized resolver. The picker rows themselves are
 * buttons, so we render the avatar via `AvatarRing` directly (no
 * nested link / no story ring).
 */
const toActorFromSearchResult = (result: ChatRecipientSearchResult): Actor => {
  const username =
    (result.type === "user" ? result.username : result.teamname) ?? "";
  return {
    id: typeof result.id === "number" ? result.id : 0,
    kind: result.type,
    username,
    displayName: result.display_name ?? null,
    avatarKey: (() => {
      const raw = result.properties_public?.avatar_key;
      return typeof raw === "string" && raw.trim().length > 0 ? raw.trim() : null;
    })(),
  };
};

interface CreateChatProps {
  isOpen: boolean;
  onClose: () => void;
  onConversationCreated?: (conversation: ChatConversation) => void;
}

const CreateChat: React.FC<CreateChatProps> = ({ isOpen, onClose, onConversationCreated }) => {
  const [search, setSearch] = useState<string>("");
  const [selectedUsers, setSelectedUsers] = useState<ChatRecipientSearchResult[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<ChatRecipientSearchResult | null>(null);
  const [groupTitle, setGroupTitle] = useState<string>("");
  const { data, isLoading } = useChatRecipientSearchQuery(search, isOpen);
  const createConversationMutation = useCreateChatConversationMutation();

  const canCreate = selectedTeam !== null || selectedUsers.length > 0;
  const isGroup = selectedTeam === null && selectedUsers.length > 1;

  const results = useMemo(() => {
    const selectedIds = new Set([
      ...selectedUsers.map((item) => `user:${item.id}`),
      ...(selectedTeam ? [`team:${selectedTeam.id}`] : []),
    ]);
    return (data || []).filter((item) => !selectedIds.has(`${item.type}:${item.id}`));
  }, [data, selectedTeam, selectedUsers]);

  const reset = (): void => {
    setSearch("");
    setSelectedUsers([]);
    setSelectedTeam(null);
    setGroupTitle("");
  };

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const handleSelectResult = (result: ChatRecipientSearchResult): void => {
    if (result.type === "team") {
      setSelectedTeam(result);
      setSelectedUsers([]);
      setGroupTitle("");
      return;
    }

    setSelectedTeam(null);
    setSelectedUsers((previous) => [...previous, result]);
  };

  const handleCreateConversation = async (): Promise<void> => {
    if (!canCreate) {
      return;
    }

    const conversation = await createConversationMutation.mutateAsync(
      selectedTeam
        ? { target_team_id: selectedTeam.id }
        : {
            participant_user_ids: selectedUsers.map((item) => item.id),
            title: isGroup && groupTitle.trim().length > 0 ? groupTitle.trim() : null,
          }
    );

    onConversationCreated?.(conversation);
    handleClose();
  };

  return (
    <ModalOrDrawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Create chat"
      stickyFooter={true}
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleCreateConversation} disabled={!canCreate || createConversationMutation.isPending}>
            {createConversationMutation.isPending ? "Creating..." : "Create"}
          </Button>
        </div>
      }
      className="chat-create-dialog"
    >
      <div className="flex flex-col gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">People or partea</label>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search usernames or parteas"
            aria-label="Search recipients"
          />
        </div>

        {selectedUsers.length > 0 || selectedTeam ? (
          <div className="space-y-2">
            <div className="text-sm font-medium">Selected</div>
            <div className="flex flex-wrap gap-2">
              {selectedUsers.map((user) => {
                const actor = toActorFromSearchResult(user);
                return (
                  <button
                    key={`user-${user.id}`}
                    type="button"
                    onClick={() => setSelectedUsers((previous) => previous.filter((item) => item.id !== user.id))}
                    className="flex items-center gap-2 rounded-full border border-eoe-border-main dark:border-eoe-dark-border-main px-3 py-1 text-sm"
                  >
                    <AvatarRing
                      size={ACTOR_AVATAR_SIZES.chip}
                      ringState="none"
                      imageUrl={resolveActorAvatarUrl(actor)}
                      imageAlt={user.display_name || user.username || "U"}
                      initials={getActorInitials(actor)}
                    />
                    <span>{user.display_name || user.username}</span>
                    <X className="size-3" />
                  </button>
                );
              })}
              {selectedTeam ? (() => {
                const teamActor = toActorFromSearchResult(selectedTeam);
                return (
                  <button
                    type="button"
                    onClick={() => setSelectedTeam(null)}
                    className="flex items-center gap-2 rounded-full border border-eoe-border-main dark:border-eoe-dark-border-main px-3 py-1 text-sm"
                  >
                    <AvatarRing
                      size={ACTOR_AVATAR_SIZES.chip}
                      ringState="none"
                      imageUrl={resolveActorAvatarUrl(teamActor)}
                      imageAlt={selectedTeam.display_name || selectedTeam.teamname || "T"}
                      initials={getActorInitials(teamActor)}
                    />
                    <span>{selectedTeam.display_name || selectedTeam.teamname}</span>
                    <X className="size-3" />
                  </button>
                );
              })() : null}
            </div>
          </div>
        ) : null}

        {isGroup ? (
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="chat-group-title">Group label (optional)</label>
            <Input
              id="chat-group-title"
              value={groupTitle}
              onChange={(event) => setGroupTitle(event.target.value)}
              placeholder="Add a name for this group"
            />
          </div>
        ) : null}

        <div className="space-y-2">
          <div className="text-sm font-medium">Results</div>
          <div className="max-h-80 overflow-y-auto rounded-xl border border-eoe-border-main dark:border-eoe-dark-border-main">
            {isLoading ? (
              <div className="p-4">
                <LoadingSkeleton variant="entity-avatar" count={4} />
              </div>
            ) : search.trim().length <= 1 ? (
              <EmptyState
                className="p-6"
                icon={<MessagesSquare />}
                title="Search to start a conversation"
                subtitle="Type at least two characters to find users or parteas."
              />
            ) : results.length === 0 ? (
              <EmptyState
                className="p-6"
                icon={<Users />}
                title="No matches"
                subtitle="Try a different username or partea name."
              />
            ) : (
              <div className="divide-y divide-eoe-border-main dark:divide-eoe-dark-border-main">
                {results.map((result) => {
                  const actor = toActorFromSearchResult(result);
                  const label =
                    result.display_name ||
                    result.username ||
                    result.teamname ||
                    "?";
                  return (
                    <button
                      key={`${result.type}-${result.id}`}
                      type="button"
                      onClick={() => handleSelectResult(result)}
                      className="flex w-full items-center justify-between gap-3 p-3 text-left transition-colors hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-bg-hover"
                    >
                      <div className="flex items-center gap-3">
                        <AvatarRing
                          size={ACTOR_AVATAR_SIZES.header}
                          ringState="none"
                          imageUrl={resolveActorAvatarUrl(actor)}
                          imageAlt={label}
                          initials={getActorInitials(actor)}
                        />
                        <div className="min-w-0">
                          <div className="truncate font-medium text-eoe-text dark:text-eoe-dark-text">
                            {label}
                          </div>
                          <div className="truncate text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {result.type === "user" ? result.username : result.teamname}
                          </div>
                        </div>
                      </div>
                      <span className="text-xs uppercase tracking-wide text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        {result.type}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalOrDrawer>
  );
};

export default CreateChat;
